import { CANCEL } from 'redux-saga';
import {
  call,
  put,
  takeEvery,
  takeLatest,
  cancel,
  cancelled,
  select,
} from 'redux-saga/effects';
import { PAGINATION } from '../../../../shared/constants';
import Mixpanel from '../../../helpers/mixpanel';

import API from '../../api';
import { UsersActionTypes, ForgotPasswordActionTypes, RequestNewPasswordActionTypes } from '../actions/types';
import {
  UsersActionCreators,
  ModalsActionCreators,
  TableActionCreators,
  MeActionCreators,
} from '../actions/creators';
import { MeSelectors } from '../selectors';
import { AsyncActionStatus } from '../enums';
import { generateErrorMessage } from '../helpers';
import { ROUTES } from '../../router';
import { Table } from '../../views/components';
import writerMatchTableAPI from '../../views/pages/WriterMatch/writerMatchTableAPI';
import allAccountsTableAPI from '../../views/pages/AllAccounts/allAccountsTableAPI';
import { Langs, userRoleCheckers } from '../../../../shared/helpers';
import { tableXlsxExport, toast } from '../../utilities';

function* fetchMoreUsers(action) {
  try {
    const {
      match,
      project,
      sort,
      skip,
      limit,
      conditions,
    } = action.payload;
    const response = yield call(
      API.Users.getMoreUsers,
      match,
      project,
      sort,
      skip,
      limit,
      conditions,
    );
    yield put(UsersActionCreators.fetchMoreUsers[AsyncActionStatus.FULFILLED](
      response.data.users,
    ));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(UsersActionCreators.fetchMoreUsers[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* searchUsers(action) {
  try {
    const {
      match,
      project,
      sort,
      conditions,
    } = action.payload;
    if (action.effect === CANCEL) {
      yield cancel();
    }
    const response = yield call(
      API.Users.searchUsers,
      match,
      project,
      sort,
      conditions,
    );
    yield put(UsersActionCreators.searchUsers[AsyncActionStatus.FULFILLED](
      response.data.users,
    ));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(UsersActionCreators.searchUsers[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  } finally {
    if (yield cancelled()) {
      yield put(UsersActionCreators.searchUsers[AsyncActionStatus.REJECTED]('The request was canceled'));
    }
  }
}

function* deleteUser(action) {
  try {
    const response = yield call(API.Users.deleteUser, action.payload.userId);
    yield call(action.payload.navigate, ROUTES.accounts.allAccounts.url);
    yield put(UsersActionCreators.deleteUser[AsyncActionStatus.FULFILLED](response.data));
    yield call(toast.success, t('toastMessages.success.userDeleted', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(UsersActionCreators.deleteUser[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* sendReinvite(action) {
  try {
    const response = yield call(API.Users.sendReinviteAfterExpire, action.payload.userId);
    yield put(UsersActionCreators.sendReinvite[AsyncActionStatus.FULFILLED](response.data));
    yield call(toast.success, t('toastMessages.success.reinviteSent', { ns: i18next.Namespace.COMMON }));
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(UsersActionCreators.sendReinvite[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* getUserById(action) {
  try {
    const response = yield call(
      API.Users.getUserById,
      action.payload.userId,
    );
    yield put(UsersActionCreators.getUserById[AsyncActionStatus.FULFILLED](response.data.user));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(UsersActionCreators.getUserById[AsyncActionStatus.REJECTED](message));
  }
}

function* updateUser(action) {
  try {
    const response = yield call(API.Users.updateUser, action.payload.userId, action.payload.user);
    yield put(UsersActionCreators.updateUser[AsyncActionStatus.FULFILLED](response.data.user));
    const me = select(MeSelectors.selectMe);

    if (response.data.user._id === me.role) {
      yield call(i18next.setCurrentLanguage, response.data.user?.primaryLanguage);
    }

    yield call(action.payload.navigate, ROUTES.accounts.accountDetails.url(response.data.user._id));
    yield call(toast.success, t('toastMessages.success.userUpdated', { ns: i18next.Namespace.COMMON }));
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(UsersActionCreators.updateUser[AsyncActionStatus.REJECTED](message));
    if (new RegExp(t('toastMessages.error.userHasProjects', { ns: i18next.Namespace.COMMON, lng: Langs.CodePrimary.EN_GB })).test(message)) {
      yield put(ModalsActionCreators.openUnableToUpdateRole());
    } else if (new RegExp(t('toastMessages.error.emailExists', { ns: i18next.Namespace.COMMON, lng: Langs.CodePrimary.EN_GB })).test(message)) {
      yield put(ModalsActionCreators.openExistingUser());
    } else {
      yield call(toast.error, message);
    }
  }
}

function* addUser(action) {
  try {
    yield call(API.Users.addUser, action.payload.user);
    yield put(UsersActionCreators.addUser[AsyncActionStatus.FULFILLED]());
    yield call(action.payload.navigate, ROUTES.accounts.allAccounts.path);
    yield call(toast.success, t('toastMessages.success.userCreated', { ns: i18next.Namespace.COMMON }));
  } catch (err) {
    const message = generateErrorMessage(err);
    yield put(UsersActionCreators.addUser[AsyncActionStatus.REJECTED](message));
    if (new RegExp(t('toastMessages.error.emailExists', { ns: i18next.Namespace.COMMON, lng: Langs.CodePrimary.EN_GB })).test(message)) {
      yield put(ModalsActionCreators.openExistingUser());
    } else {
      yield call(toast.error, message);
    }
  }
}

function* getUsers(action) {
  try {
    const response = yield call(
      API.Users.getUsers,
      action.payload.filters,
      action.payload.pagination,
      action.payload.sortBy,
    );

    if (ROUTES.accounts.allAccounts.path === window.location.pathname) {
      yield put(UsersActionCreators.getUsers[AsyncActionStatus.FULFILLED](response.data.users));
      yield put(TableActionCreators.setPagination({
        pageIndex: response.data.pageIndex,
        pageSize: response.data.pageSize,
        pageCount: response.data.pageCount,
        rowsTotalCount: response.data.rowsTotalCount,
      }));
      yield put(TableActionCreators.setSortBy(
        Table.convertSortByForTable(response.data.sortBy, allAccountsTableAPI.columnsFixtures),
      ));
    } else {
      yield put(UsersActionCreators.getUsers[AsyncActionStatus.REJECTED]());
    }
  } catch (err) {
    yield put(UsersActionCreators.getUsers[AsyncActionStatus.REJECTED](
      generateErrorMessage(err),
    ));
  }
}

function* getUsersForExcel(action) {
  try {
    const response = yield call(
      API.Users.getUsersForExcel,
      action.payload.filters,
      action.payload.sortBy,
    );

    tableXlsxExport(response.data.users, 'All Accounts', allAccountsTableAPI);

    yield put(UsersActionCreators.getUsersForExcel[AsyncActionStatus.FULFILLED]());
  } catch (err) {
    yield put(UsersActionCreators.getUsersForExcel[AsyncActionStatus.REJECTED](
      generateErrorMessage(err),
    ));
  }
}

function* getWritersMatch(action) {
  try {
    const response = yield call(
      API.Projects.getWritersMatch,
      action.payload.projectId,
      action.payload.pagination,
      action.payload.sortBy,
      action.payload.filters,
    );
    if (ROUTES.projects.writerMatch.url(action.payload.projectId) === window.location.pathname) {
      yield put(UsersActionCreators.getWritersMatch[AsyncActionStatus.FULFILLED](response.data.writers));
      yield put(TableActionCreators.setPagination({
        pageIndex: response.data.pageIndex,
        pageSize: response.data.pageSize,
        pageCount: response.data.pageCount,
        rowsTotalCount: response.data.rowsTotalCount,
      }));
      yield put(TableActionCreators.setSortBy(
        Table.convertSortByForTable(response.data.sortBy, writerMatchTableAPI.columnsFixtures),
      ));
    } else {
      yield put(UsersActionCreators.getWritersMatch[AsyncActionStatus.REJECTED]());
    }
  } catch (error) {
    yield put(UsersActionCreators.getWritersMatch[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* getExcelWritersMatch(action) {
  try {
    const response = yield call(
      API.Projects.getExcelWritersMatch,
      action.payload.projectId,
      action.payload.sortBy,
      action.payload.filters,
    );
    const { writers, customerPostalCode } = response.data;
    tableXlsxExport(writers, `Writers-${customerPostalCode}`, writerMatchTableAPI, action.payload.userRole);
    yield put(UsersActionCreators.getExcelWritersMatch[AsyncActionStatus.FULFILLED]());
  } catch (error) {
    yield put(UsersActionCreators.getExcelWritersMatch[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* getOfflineWritersMatch(action) {
  try {
    const response = yield call(
      API.Users.getOfflineWritersMatch,
      action.payload.pagination,
      action.payload.sortBy,
      action.payload.filters,
      action.payload.address,
    );

    if (window.location.pathname === '/offline-writer-match') {
      yield put(UsersActionCreators.getOfflineWritersMatch[AsyncActionStatus.FULFILLED](response.data.writers));
      yield put(TableActionCreators.setPagination({
        pageIndex: response.data.pageIndex,
        pageSize: response.data.pageSize,
        pageCount: response.data.pageCount,
        rowsTotalCount: response.data.rowsTotalCount,
      }));
      yield put(TableActionCreators.setSortBy(
        Table.convertSortByForTable(response.data.sortBy, writerMatchTableAPI.columnsFixtures),
      ));
    } else {
      yield put(UsersActionCreators.getOfflineWritersMatch[AsyncActionStatus.REJECTED]());
    }
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(UsersActionCreators.getOfflineWritersMatch[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* getExcelOfflineWritersMatch(action) {
  try {
    const response = yield call(
      API.Users.getExcelOfflineWritersMatch,
      action.payload.sortBy,
      action.payload.filters,
      action.payload.address,
    );
    const { writers, customerPostalCode } = response.data;
    tableXlsxExport(writers, `Writers-${customerPostalCode}`, writerMatchTableAPI, action.payload.userRole);
    yield put(UsersActionCreators.getExcelOfflineWritersMatch[AsyncActionStatus.FULFILLED]());
  } catch (error) {
    yield put(UsersActionCreators.getExcelOfflineWritersMatch[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* addWriterToShortlist(action) {
  try {
    const response = yield call(
      API.Projects.addWriterToShortlist,
      action.payload.projectId,
      action.payload.writerId,
    );
    yield put(UsersActionCreators.addWriterToShortlist[AsyncActionStatus.FULFILLED](response.data.writer));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(UsersActionCreators.addWriterToShortlist[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* removeWriterFromShortlist(action) {
  try {
    const response = yield call(
      API.Projects.removeWriterFromShortlist,
      action.payload.projectId,
      action.payload.writerId,
    );
    yield put(UsersActionCreators.removeWriterFromShortlist[AsyncActionStatus.FULFILLED](response.data.writer));
  } catch (error) {
    const message = generateErrorMessage(error);
    yield put(UsersActionCreators.removeWriterFromShortlist[AsyncActionStatus.REJECTED](message));
    yield call(toast.error, message);
  }
}

function* getUserWithMiscData(action) {
  try {
    const response = yield call(
      API.Users.getUserWithMiscData,
      action.payload.userId,
      action.payload.pagination,
      action.payload.shouldLoadMoreProjects,
    );
    let {
      user, projects, ratings, pagination, shouldLoadMoreProjects,
    } = response.data;
    user = user && typeof user === 'object' && user instanceof Object ? user : {};
    projects = Array.isArray(projects) ? projects : [];
    ratings = Array.isArray(ratings) ? ratings : [];
    pagination = pagination && typeof pagination === 'object' && pagination instanceof Object ? pagination : {
      pageIndex: PAGINATION.pageIndex,
      pageSize: PAGINATION.pageSize,
      pageCount: Math.ceil(projects.length / PAGINATION.pageSize),
      rowsTotalCount: projects.length,
    };
    shouldLoadMoreProjects = Boolean(shouldLoadMoreProjects);
    yield put(UsersActionCreators.getUserWithMiscData[AsyncActionStatus.FULFILLED](
      user,
      projects,
      ratings,
      shouldLoadMoreProjects,
    ));
    yield put(TableActionCreators.setPagination({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      pageCount: pagination.pageCount,
      rowsTotalCount: pagination.rowsTotalCount,
    }));
  } catch (error) {
    yield put(UsersActionCreators.getUserWithMiscData[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

function* toggleArchivingUser(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(API.Users.toggleArchivingUser, userId);
    yield put(UsersActionCreators.toggleArchivingUser[AsyncActionStatus.FULFILLED](response.data));
    if (response.data.isArchived) yield call(toast.success, t('toastMessages.success.userArchived', { ns: i18next.Namespace.COMMON }));
    else yield call(toast.success, t('toastMessages.success.userUnarchived', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    let errMsg = generateErrorMessage(error);
    if (error.response?.data?.status === 400) errMsg = t('toastMessages.error.userHasProject', { ns: i18next.Namespace.COMMON }); // TODO: Change approach to handle errors for toast
    yield put(UsersActionCreators.toggleArchivingUser[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* removeAvatar(action) {
  try {
    const { userId, photoHandle } = action.payload;
    const response = yield call(API.Photos.removeAvatar, userId, photoHandle);
    yield put(UsersActionCreators.removeAvatar[AsyncActionStatus.FULFILLED](response.data));
    yield call(toast.success, t('toastMessages.success.photoRemoved', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const errMsg = generateErrorMessage(error);
    yield put(UsersActionCreators.removeAvatar[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* removeUnsavedUserAvatar(action) {
  try {
    const { photoHandle } = action.payload;
    yield call(API.Photos.removeUnsavedUserAvatar, photoHandle);
    yield put(UsersActionCreators.removeUnsavedUserAvatar[AsyncActionStatus.FULFILLED]());
  } catch (error) {
    const errMsg = generateErrorMessage(error);
    yield put(UsersActionCreators.removeUnsavedUserAvatar[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* addAvatarPhoto(action) {
  try {
    const { userId, photo } = action.payload;
    const response = yield call(API.Users.addAvatarPhoto, userId, photo);
    yield put(UsersActionCreators.addAvatarPhoto[AsyncActionStatus.FULFILLED](response.data.user));
    yield call(toast.success, t('toastMessages.success.photoAdded', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const errMsg = generateErrorMessage(error);
    yield put(UsersActionCreators.addAvatarPhoto[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* forgotPassword(action) {
  try {
    const { email } = action.payload;
    yield call(API.Users.forgotPassword, email);
    yield put(UsersActionCreators.forgotPassword[AsyncActionStatus.FULFILLED]());
    yield call(toast.success, t('toastMessages.success.resetPasswordSent', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const errMsg = generateErrorMessage(error);
    yield put(UsersActionCreators.forgotPassword[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* setupPassword(action) {
  const {
    email, token, password, isPolicyAccepted, navigate,
  } = action.payload;

  try {
    const response = yield call(API.Users.passwordSetup, token, password, isPolicyAccepted);
    const { data: { user, isFirstLogin } = {} } = response;

    Mixpanel.identify(user._id);

    const userProfileData = {
      $userEmail: user.email,
      $userRole: user.role,
      userId: user._id,
    };
    if (userRoleCheckers.isCustomer(user.role) && user.project?.type) {
      userProfileData.$projectType = user.project.type;
    }

    Mixpanel.people.set(userProfileData);
    Mixpanel.track('Password details saved');
    if (isFirstLogin) Mixpanel.track('First user log in');

    yield put(UsersActionCreators.setupPassword[AsyncActionStatus.FULFILLED]());
    yield call(i18next.setCurrentLanguage, user.primaryLanguage);
    yield put(MeActionCreators.loginUser[AsyncActionStatus.FULFILLED](user));
    if (userRoleCheckers.isCustomer(user.role) && !user.project?.type) {
      yield call(navigate, ROUTES.noProjectForCustomer, { replace: true });
    }
  } catch (err) {
    Mixpanel.track('Set password error', { providedEmail: email });
    const errMsg = generateErrorMessage(err);
    yield put(UsersActionCreators.setupPassword[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* getUserByToken(action) {
  try {
    const response = yield call(API.Users.getUserByToken, action.payload.token);
    yield put(UsersActionCreators.getUserByToken[AsyncActionStatus.FULFILLED](response.data));
    yield call(i18next.setCurrentLanguage, response.data?.primaryLanguage);
  } catch (err) {
    const errMsg = generateErrorMessage(err);
    yield put(UsersActionCreators.getUserByToken[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* updateHubspotProjectsCount(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(API.Users.updateHubspotProjectsCount, userId);
    yield put(UsersActionCreators.updateHubspotProjectsCount[AsyncActionStatus.FULFILLED](response.data));
    yield call(toast.success, t('toastMessages.success.projectCountUpdated', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const errMsg = generateErrorMessage(error);
    yield put(UsersActionCreators.updateHubspotProjectsCount[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* updateWritersProjectCount() {
  try {
    const response = yield call(API.Users.updateWritersProjectCount);
    yield put(UsersActionCreators.updateWritersProjectCount[AsyncActionStatus.FULFILLED](response.data));
    yield call(toast.success, t('toastMessages.success.writersProjectCountUpdated', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const errMsg = generateErrorMessage(error);
    yield put(UsersActionCreators.updateWritersProjectCount[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* updateWritersDBSScreened() {
  try {
    yield call(API.Users.updateWritersDBSScreened);
    yield put(UsersActionCreators.updateWritersDBSScreened[AsyncActionStatus.FULFILLED]());
    yield call(toast.success, t('toastMessages.success.dbsScreenedUpdated', { ns: i18next.Namespace.COMMON }));
  } catch (error) {
    const errMsg = generateErrorMessage(error);
    yield put(UsersActionCreators.updateWritersDBSScreened[AsyncActionStatus.REJECTED](errMsg));
    yield call(toast.error, errMsg);
  }
}

function* requestNewPassword(action) {
  try {
    let response;
    if (action.payload.isSetup) {
      response = yield call(API.Users.askForInvitation, action.payload.token, action.payload.email);
    } else {
      response = yield call(API.Users.forgotPassword, action.payload.email);
    }
    yield put(UsersActionCreators.requestNewPassword[AsyncActionStatus.FULFILLED]());
    yield call(toast.success, response.data.message);
  } catch (err) {
    const { message } = err;
    yield put(UsersActionCreators.requestNewPassword[AsyncActionStatus.REJECTED](err));
    yield call(toast.error, message);
  }
}

export default [
  takeEvery(
    UsersActionTypes.getUsers[AsyncActionStatus.PENDING],
    getUsers,
  ),
  takeEvery(
    UsersActionTypes.getUsersForExcel[AsyncActionStatus.PENDING],
    getUsersForExcel,
  ),
  takeLatest(
    UsersActionTypes.fetchMoreUsers[AsyncActionStatus.PENDING],
    fetchMoreUsers,
  ),
  takeLatest(
    UsersActionTypes.searchUsers[AsyncActionStatus.PENDING],
    searchUsers,
  ),
  takeEvery(
    UsersActionTypes.deleteUser[AsyncActionStatus.PENDING],
    deleteUser,
  ),
  takeEvery(
    UsersActionTypes.sendReinvite[AsyncActionStatus.PENDING],
    sendReinvite,
  ),
  takeEvery(
    UsersActionTypes.getUserById[AsyncActionStatus.PENDING],
    getUserById,
  ),
  takeEvery(
    UsersActionTypes.updateUser[AsyncActionStatus.PENDING],
    updateUser,
  ),
  takeEvery(
    UsersActionTypes.addUser[AsyncActionStatus.PENDING],
    addUser,
  ),
  takeEvery(
    UsersActionTypes.getWritersMatch[AsyncActionStatus.PENDING],
    getWritersMatch,
  ),
  takeEvery(
    UsersActionTypes.getOfflineWritersMatch[AsyncActionStatus.PENDING],
    getOfflineWritersMatch,
  ),
  takeEvery(
    UsersActionTypes.addWriterToShortlist[AsyncActionStatus.PENDING],
    addWriterToShortlist,
  ),
  takeEvery(
    UsersActionTypes.removeWriterFromShortlist[AsyncActionStatus.PENDING],
    removeWriterFromShortlist,
  ),
  takeEvery(
    UsersActionTypes.getUserWithMiscData[AsyncActionStatus.PENDING],
    getUserWithMiscData,
  ),
  takeEvery(
    UsersActionTypes.toggleArchivingUser[AsyncActionStatus.PENDING],
    toggleArchivingUser,
  ),
  takeEvery(
    UsersActionTypes.removeAvatar[AsyncActionStatus.PENDING],
    removeAvatar,
  ),
  takeEvery(
    UsersActionTypes.removeUnsavedUserAvatar[AsyncActionStatus.PENDING],
    removeUnsavedUserAvatar,
  ),
  takeEvery(
    UsersActionTypes.addAvatarPhoto[AsyncActionStatus.PENDING],
    addAvatarPhoto,
  ),
  takeEvery(
    UsersActionTypes.getExcelWritersMatch[AsyncActionStatus.PENDING],
    getExcelWritersMatch,
  ),
  takeEvery(
    UsersActionTypes.getExcelOfflineWritersMatch[AsyncActionStatus.PENDING],
    getExcelOfflineWritersMatch,
  ),
  takeEvery(
    ForgotPasswordActionTypes.forgotPassword[AsyncActionStatus.PENDING],
    forgotPassword,
  ),
  takeEvery(
    UsersActionTypes.setupPassword[AsyncActionStatus.PENDING],
    setupPassword,
  ),
  takeEvery(
    UsersActionTypes.getUserByToken[AsyncActionStatus.PENDING],
    getUserByToken,
  ),
  takeEvery(
    UsersActionTypes.updateHubspotProjectsCount[AsyncActionStatus.PENDING],
    updateHubspotProjectsCount,
  ),
  takeEvery(
    UsersActionTypes.updateWritersProjectCount[AsyncActionStatus.PENDING],
    updateWritersProjectCount,
  ),
  takeEvery(
    UsersActionTypes.updateWritersDBSScreened[AsyncActionStatus.PENDING],
    updateWritersDBSScreened,
  ),
  takeEvery(
    RequestNewPasswordActionTypes.requestNewPassword[AsyncActionStatus.PENDING],
    requestNewPassword,
  ),
];
